/**
 * Step 3 - Compensation & Benefits Component
 */

import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { CURRENCIES, COMMON_BENEFITS } from '../constants';
import formService from '../../../services/formService';
import useAuth from '../../../hooks/useAuth';

export const Step3Compensation = React.memo(({ jobData, errors, onUpdate, onBenefitToggle }) => {
  const { user } = useAuth();
  const [publishedForms, setPublishedForms] = useState([]);
  const [loadingForms, setLoadingForms] = useState(false);

  // Load published forms for the form selector
  useEffect(() => {
    const loadForms = async () => {
      if (!user?.id) return;
      
      setLoadingForms(true);
      try {
        const result = await formService.getEmployerForms(user.id);
        if (result.success) {
          // Filter to only show published forms
          const published = (result.data || []).filter(form => form.status === 'published');
          setPublishedForms(published);
        }
      } catch (error) {
        console.error('Error loading forms:', error);
      } finally {
        setLoadingForms(false);
      }
    };

    loadForms();
  }, [user?.id]);

  const handleInputChange = (field, value) => {
    onUpdate({ [field]: value });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-text-primary mb-4">
          Compensation & Benefits
        </h3>

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Input
                label="Minimum Salary (per hour)"
                type="number"
                value={jobData.salary_min}
                onChange={(e) => handleInputChange('salary_min', e.target.value)}
                placeholder="15"
                error={errors.salary_min}
                required
              />
            </div>
            <div>
              <Input
                label="Maximum Salary (per hour)"
                type="number"
                value={jobData.salary_max}
                onChange={(e) => handleInputChange('salary_max', e.target.value)}
                placeholder="25"
                error={errors.salary_max}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Currency
              </label>
              <select
                value={jobData.currency}
                onChange={(e) => handleInputChange('currency', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              >
                {CURRENCIES.map(curr => (
                  <option key={curr.value} value={curr.value}>
                    {curr.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Benefits & Perks
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {COMMON_BENEFITS.map(benefit => (
                <button
                  key={benefit}
                  type="button"
                  onClick={() => onBenefitToggle(benefit)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium duration-200 ${
                    jobData.benefits.includes(benefit)
                      ? 'bg-primary text-white'
                      : 'bg-secondary-100 text-text-secondary hover:bg-secondary-200'
                  }`}
                >
                  {benefit}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Input
                label="Application Deadline"
                type="date"
                value={jobData.application_deadline}
                onChange={(e) => handleInputChange('application_deadline', e.target.value)}
              />
            </div>
            <div>
              <Input
                label="Expected Start Date"
                type="date"
                value={jobData.start_date}
                onChange={(e) => handleInputChange('start_date', e.target.value)}
              />
            </div>
          </div>

          {/* Application Form Selector */}
          <div className="pt-6 border-t border-border">
            <label className="block text-sm font-medium text-text-primary mb-2">
              <Icon name="FileText" size={16} className="inline mr-2" />
              Application Form (Optional)
            </label>
            <p className="text-xs text-text-secondary mb-3">
              Require applicants to answer additional questions when applying to this job
            </p>
            
            {loadingForms ? (
              <div className="flex items-center text-sm text-text-secondary">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
                Loading forms...
              </div>
            ) : publishedForms.length > 0 ? (
              <select
                value={jobData.selected_form_id || ''}
                onChange={(e) => handleInputChange('selected_form_id', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-background"
              >
                <option value="">No form required</option>
                {publishedForms.map(form => (
                  <option key={form.id} value={form.id}>
                    {form.title} ({form.questions?.length || 0} question{form.questions?.length !== 1 ? 's' : ''})
                  </option>
                ))}
              </select>
            ) : (
              <div className="flex items-center justify-between p-3 bg-secondary-50 border border-border rounded-lg">
                <div className="flex items-center text-sm text-text-secondary">
                  <Icon name="Info" size={16} className="mr-2" />
                  <span>No published forms available</span>
                </div>
                <button
                  type="button"
                  onClick={() => window.open('/form-builder', '_blank')}
                  className="text-sm text-primary hover:text-primary-dark underline"
                >
                  Create a form
                </button>
              </div>
            )}
            
            {jobData.selected_form_id && (
              <div className="mt-2 text-xs text-primary flex items-center">
                <Icon name="CheckCircle" size={14} className="mr-1" />
                <span>VAs will be required to answer form questions when applying</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

Step3Compensation.displayName = 'Step3Compensation';
